import { Switch } from "../../components/ui/switch";
import { Bell, EnvelopeSimple, Lightning } from "@phosphor-icons/react";

const CHANNELS = [
  { key: "portal", label: "In Portal", icon: Bell },
  { key: "email", label: "Email", icon: EnvelopeSimple },
];

export function ClientNotificationMatrix({ data, push }) {
  const toggle = (key, channel, value) =>
    push({ prefs: { [key]: { [channel]: value } } }, (d) => ({ ...d, types: d.types.map((t) => (t.key === key ? { ...t, [channel]: value } : t)) }));

  const setAll = (channel, value) =>
    push({ prefs: Object.fromEntries(data.types.map((t) => [t.key, { [channel]: value }])) }, (d) => ({ ...d, types: d.types.map((t) => ({ ...t, [channel]: value })) }));

  const setMasterEmail = (value) => push({ notify_email: value }, (d) => ({ ...d, notify_email: value }));

  const emailsPaused = data.notify_email === false;

  return (
    <div className="space-y-4" data-testid="notification-matrix">
      <div className={`flex items-center justify-between gap-3 rounded-md border p-3 ${emailsPaused ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20" : "border-border"}`} data-testid="notif-master-email">
        <div>
          <p className="text-xs font-semibold">Email notifications</p>
          <p className="text-[11px] text-muted-foreground">{emailsPaused ? "Paused — nothing is emailed to you until you switch this back on." : "Switch off to pause every email without losing your choices below."}</p>
        </div>
        <Switch checked={!emailsPaused} onCheckedChange={setMasterEmail} data-testid="notif-master-email-toggle" />
      </div>

      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full text-sm min-w-[520px]" data-testid="notif-prefs-table">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="py-2 px-4 sm:px-3 text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">Notification</th>
              {CHANNELS.map((c) => (
                <th key={c.key} className="py-2 px-3 w-32 text-center" data-testid={`notif-col-${c.key}`}>
                  <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground"><c.icon size={14} weight="duotone" /> {c.label}</div>
                  <div className="flex justify-center gap-1 mt-1 text-[10px]">
                    <button type="button" onClick={() => setAll(c.key, true)} className="text-primary hover:underline" data-testid={`notif-all-on-${c.key}`}>All on</button>
                    <span className="text-muted-foreground">·</span>
                    <button type="button" onClick={() => setAll(c.key, false)} className="text-primary hover:underline" data-testid={`notif-all-off-${c.key}`}>All off</button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.types.map((t) => (
              <tr key={t.key} className="border-b border-border/60 last:border-0 hover:bg-muted/30 transition-colors" data-testid={`notif-row-${t.key}`}>
                <td className="py-3 px-4 sm:px-3">
                  <p className="text-xs font-semibold flex items-center gap-1.5">{t.label}
                    {t.urgent && <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-semibold text-amber-600" title="Always emailed immediately — never held or digested" data-testid={`notif-urgent-${t.key}`}><Lightning size={10} weight="fill" /> Immediate</span>}
                  </p>
                  <p className="text-[11px] text-muted-foreground">{t.description}</p>
                </td>
                {CHANNELS.map((c) => (
                  <td key={c.key} className="py-3 px-3 text-center">
                    <Switch checked={!!t[c.key]} onCheckedChange={(v) => toggle(t.key, c.key, v)} disabled={c.key === "email" && emailsPaused}
                            aria-label={`${t.label} — ${c.label}`} data-testid={`notif-pref-${t.key}-${c.key}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground" data-testid="notif-prefs-footnote">Changes save instantly. Sign-off requests and account security messages are always sent.</p>
    </div>
  );
}
